import { useState } from 'react';
import { useSetMtoMaterialMultiplicityMutation } from '@shared/api';
import type { MtoMaterialPosition } from '@entities/mto';
import { apiErrorMessage, positionRaw } from '@entities/mto';
import { Button } from '@shared/ui/Button';
import { Actions, MultForm, MultInput } from '../styles';

/** Кратность строки «на 1 элемент» — сколько таких конструкций на объекте.
 *  Снабженец вписывает её, когда в РД числа нет или их несколько; строка
 *  уходит в сверку с заявками умноженной, итог «с учётом кратности»
 *  пересчитывается. Указанную можно снять — вернётся найденная по РД. */
export function MultEditor({
  position,
  runId,
  onError,
}: {
  position: MtoMaterialPosition;
  runId: string;
  onError: (msg: string) => void;
}) {
  const r = positionRaw(position);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(
    r.multiplicity_manual != null ? String(r.multiplicity_manual) : '',
  );
  const [save, { isLoading }] = useSetMtoMaterialMultiplicityMutation();

  async function submit(factor: number | null) {
    onError('');
    try {
      await save({ id: position.id, runId, factor }).unwrap();
      setOpen(false);
    } catch (err) {
      onError(
        `«${position.name}»: ${apiErrorMessage(err) ?? 'не удалось сохранить кратность, попробуйте ещё раз.'}`,
      );
    }
  }

  const parsed = Number(value.replace(',', '.').trim());
  const valid = value.trim() !== '' && Number.isFinite(parsed) && parsed > 0;
  // Кратность уже есть (по РД или указана) — её меняют; нет — указывают.
  const hasFactor = r.multiplicity_manual != null || r.multiplicity?.status === 'found';

  if (!open) {
    return (
      <Actions>
        <Button
          type="button"
          $variant="ghost"
          $size="sm"
          disabled={isLoading}
          onClick={() => setOpen(true)}
          aria-label={`Указать кратность: ${position.name}`}
          title="Сколько таких конструкций на объекте — строка войдёт в сверку умноженной"
        >
          {hasFactor ? 'Изменить кратность' : 'Указать кратность'}
        </Button>
        {r.multiplicity_manual != null && (
          <Button
            type="button"
            $variant="ghost"
            $size="sm"
            disabled={isLoading}
            onClick={() => submit(null)}
            aria-label={`Снять указанную кратность: ${position.name}`}
          >
            Сбросить
          </Button>
        )}
      </Actions>
    );
  }
  return (
    <MultForm
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit(parsed);
      }}
    >
      ×
      <MultInput
        inputMode="decimal"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="шт"
        aria-label={`Кратность — сколько таких конструкций: ${position.name}`}
      />
      <Button type="submit" $variant="secondary" $size="sm" disabled={!valid || isLoading}>
        Сохранить
      </Button>
      <Button type="button" $variant="ghost" $size="sm" onClick={() => setOpen(false)}>
        Отмена
      </Button>
    </MultForm>
  );
}
